import { motion } from "motion/react";
import {
    Plus,
    Trash,
    Globe,
    Building,
    FileText,
    Users,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Banknote,
    Search,
    Zap,
    Lock,
    Unlock,
    DollarSign,
    TrendingUp
} from "lucide-react";
import { useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

import { useRWAStore, Asset, KycStatus } from "../store/rwa-store";

export default function AdminView() {
    const { assets, users, transactions, addAsset, deleteAsset, approveKyc, freezeUser, unfreezeUser } = useRWAStore();
    const [activeTab, setActiveTab] = useState<'overview' | 'assets' | 'users' | 'dividends'>('overview');
    const [searchTerm, setSearchTerm] = useState("");
    const [filterType, setFilterType] = useState("all");

    // --- ASSET FACTORY STATE ---
    const [isFactoryOpen, setIsFactoryOpen] = useState(false);
    const [newAsset, setNewAsset] = useState<Partial<Asset>>({ type: 'rwa' });

    // --- CALCULATED STATS ---
    const totalAssetValue = assets.reduce((acc, curr) => acc + (curr.price || 0), 0); // Simplified marketcap
    const totalUsers = users.length;
    const pendingKyc = users.filter(u => u.kycStatus === 'pending').length;
    const totalYieldPaid = transactions.filter(t => t.type === 'distribution').reduce((acc, curr) => acc + curr.amount, 0);

    // Mock Chart Data
    const activityData = [
        { name: 'Mon', active: 400, yield: 240 },
        { name: 'Tue', active: 300, yield: 139 },
        { name: 'Wed', active: 200, yield: 980 },
        { name: 'Thu', active: 278, yield: 390 },
        { name: 'Fri', active: 189, yield: 480 },
        { name: 'Sat', active: 239, yield: 380 },
        { name: 'Sun', active: 349, yield: 430 },
    ];

    const filteredAssets = assets.filter(a =>
        (filterType === 'all' || a.type === filterType) &&
        (a.name.toLowerCase().includes(searchTerm.toLowerCase()) || a.symbol.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const handleIssueAsset = (e: React.FormEvent) => {
        e.preventDefault();
        if (newAsset.name && newAsset.symbol && newAsset.price) {
            addAsset({
                ...newAsset as Asset,
                id: Math.random().toString(36).substring(2, 11),
                change24h: newAsset.change24h ?? 0,
            });
            setIsFactoryOpen(false);
            setNewAsset({ type: 'rwa', name: '', symbol: '', price: 0 });
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 p-6">
            {/* TOP BAR */}
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-neutral-800 pb-6">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight mb-1">Command Center</h1>
                    <p className="text-neutral-400 flex items-center gap-2 text-sm">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                        System Operational. {users.length} Active Nodes.
                    </p>
                </div>
                <div className="flex bg-neutral-900/50 p-1 rounded-xl border border-neutral-800 backdrop-blur-xs">
                    {['overview', 'assets', 'users', 'dividends'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab as 'overview' | 'assets' | 'users' | 'dividends')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize ${activeTab === tab
                                ? 'bg-neutral-800 text-white shadow-lg shadow-black/50 border border-neutral-700/50'
                                : 'text-neutral-500 hover:text-neutral-300'
                                }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </header>

            {/* --- OVERVIEW DASHBOARD --- */}
            {activeTab === 'overview' && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                    {/* STAT CARDS */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <StatsCard title="Total Asset Value" value={`$${(totalAssetValue * 1250).toLocaleString()}`} trend="+4.5%" icon={DollarSign} />
                        <StatsCard title="Registered Investors" value={totalUsers.toString()} trend="+12" icon={Users} />
                        <StatsCard title="Pending KYC" value={pendingKyc.toString()} trend={pendingKyc > 0 ? "Action Req" : "Clear"} isAlert={pendingKyc > 0} icon={AlertTriangle} />
                        <StatsCard title="Yield Distributed" value={`$${totalYieldPaid.toLocaleString()}`} trend="Year to Date" icon={Banknote} />
                    </div>

                    {/* CHARTS ROW */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6 backdrop-blur-xs">
                            <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-emerald-500" /> Platform Activity
                            </h3>
                            <div className="h-64 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={activityData}>
                                        <defs>
                                            <linearGradient id="colorYield" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <XAxis dataKey="name" stroke="#525252" fontSize={12} tickLine={false} axisLine={false} />
                                        <YAxis stroke="#525252" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `$${value}`} />
                                        <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                                        <Tooltip
                                            contentStyle={{ backgroundColor: '#171717', border: '1px solid #262626', borderRadius: '8px' }}
                                        />
                                        <Area type="monotone" dataKey="yield" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorYield)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* RECENT ALERTS / ACTIONS */}
                        <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6 backdrop-blur-xs flex flex-col">
                            <h3 className="text-lg font-bold mb-4">System Stream</h3>
                            <div className="flex-1 overflow-y-auto space-y-4 max-h-64 pr-2">
                                {transactions.slice(0, 5).map(tx => (
                                    <div key={tx.id} className="flex gap-3 items-start p-3 rounded-lg bg-neutral-950/50 border border-neutral-800 text-sm">
                                        <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${tx.status === 'completed' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                                        <div>
                                            <div className="text-white font-medium capitalize">{tx.type} Event</div>
                                            <div className="text-neutral-500 text-xs">
                                                {tx.currency} {tx.amount} • {new Date(tx.timestamp).toLocaleTimeString()}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {transactions.length === 0 && <div className="text-neutral-500 italic text-sm">No recent system events.</div>}
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* --- ASSETS TAB --- */}
            {activeTab === 'assets' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                    <div className="flex flex-col md:flex-row gap-4 justify-between items-center bg-neutral-900/30 p-4 rounded-xl border border-neutral-800">
                        <div className="flex gap-2 w-full md:w-auto">
                            <div className="relative group">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 group-focus-within:text-emerald-500 transition-colors" />
                                <input
                                    placeholder="Search assets..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9 pr-4 py-2 bg-neutral-950 border border-neutral-800 rounded-lg text-sm focus:outline-hidden focus:border-emerald-500 w-full md:w-64 transition-all"
                                />
                            </div>
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                className="px-4 py-2 bg-neutral-950 border border-neutral-800 rounded-lg text-sm focus:outline-hidden focus:border-emerald-500"
                            >
                                <option value="all">All Types</option>
                                <option value="rwa">Real World Assets</option>
                                <option value="crypto">Digital Tokens</option>
                            </select>
                        </div>
                        <button
                            onClick={() => setIsFactoryOpen(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-bold rounded-lg hover:bg-neutral-200 transition-colors shadow-[0_0_15px_rgba(255,255,255,0.1)]"
                        >
                            <Plus className="w-4 h-4" /> Issue New Asset
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredAssets.map((asset) => (
                            <div key={asset.id} className="group relative overflow-hidden bg-neutral-900/30 border border-neutral-800 rounded-2xl p-6 hover:border-emerald-500/30 transition-all hover:bg-neutral-900/50">
                                <div className="flex justify-between items-start mb-6">
                                    <div className={`p-3 rounded-xl ${asset.type === 'crypto' ? 'bg-orange-500/10 text-orange-500' : 'bg-blue-500/10 text-blue-500'}`}>
                                        {asset.type === 'crypto' ? <Globe className="w-6 h-6" /> : <Building className="w-6 h-6" />}
                                    </div>
                                    <div className="transform translate-x-10 group-hover:translate-x-0 transition-transform opacity-0 group-hover:opacity-100 flex gap-2">
                                        <button className="p-2 bg-neutral-800 hover:bg-neutral-700 rounded-lg text-neutral-400 hover:text-white" title="View Docs">
                                            <FileText className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => deleteAsset(asset.id)} className="p-2 bg-red-900/20 hover:bg-red-900/40 text-red-500 rounded-lg" title="Delist">
                                            <Trash className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <h3 className="font-bold text-lg text-white">{asset.name}</h3>
                                    <div className="text-xs font-mono text-emerald-500 uppercase tracking-wider">{asset.symbol}</div>
                                </div>
                                <div className="flex justify-between items-end border-t border-neutral-800/50 pt-4 mt-4">
                                    <div>
                                        <div className="text-xs text-neutral-500 uppercase mb-1">Price</div>
                                        <div className="font-mono font-bold">${asset.price.toLocaleString()}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs text-neutral-500 uppercase mb-1">Change (24h)</div>
                                        <div className={`font-mono font-bold text-sm ${asset.change24h >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                            {asset.change24h > 0 ? '+' : ''}{asset.change24h}%
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            )}

            {/* --- USERS TAB --- */}
            {activeTab === 'users' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                    <div className="bg-neutral-900/30 border border-neutral-800 rounded-2xl overflow-hidden backdrop-blur-xs">
                        <table className="w-full text-left">
                            <thead className="bg-neutral-950/50 border-b border-neutral-800 text-xs uppercase tracking-wider text-neutral-500 font-medium">
                                <tr>
                                    <th className="p-6">Identity</th>
                                    <th className="p-6">Role</th>
                                    <th className="p-6">KYC Status</th>
                                    <th className="p-6">Wallet Activity</th>
                                    <th className="p-6 text-right">Controls</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-800/50">
                                {users.map(user => (
                                    <tr key={user.id} className="hover:bg-neutral-900/50 transition-colors group">
                                        <td className="p-6">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-linear-to-br from-neutral-800 to-neutral-950 flex items-center justify-center border border-neutral-800 text-xs font-bold ring-2 ring-transparent group-hover:ring-emerald-500/20 transition-all">
                                                    {user.name.charAt(0)}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-white text-sm">{user.name}</div>
                                                    <div className="text-xs text-neutral-500 font-mono">{user.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-6">
                                            <span className={`px-2 py-1 rounded-md text-[10px] uppercase font-bold tracking-wider border ${user.role === 'admin'
                                                ? 'bg-purple-900/20 border-purple-500/20 text-purple-400'
                                                : 'bg-neutral-800/50 border-neutral-700 text-neutral-400'
                                                }`}>
                                                {user.role}
                                            </span>
                                        </td>
                                        <td className="p-6">
                                            <StatusBadge status={user.kycStatus} />
                                        </td>
                                        <td className="p-6">
                                            <div className="font-mono text-xs text-neutral-400">
                                                {(user.wallet?.balances?.USD ?? 0) > 0 ? (
                                                    <span className="text-emerald-400">Active Investor</span>
                                                ) : (
                                                    <span className="text-neutral-600">No Balance</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-6 text-right">
                                            <div className="flex justify-end gap-2 opacity-50 group-hover:opacity-100 transition-opacity">
                                                {user.kycStatus === 'pending' && (
                                                    <button onClick={() => approveKyc(user.id)} className="p-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 rounded-lg">
                                                        <CheckCircle className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {user.kycStatus === 'verified' && (
                                                    <button onClick={() => freezeUser(user.id)} className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-lg" title="Freeze Account">
                                                        <Lock className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {user.kycStatus === 'frozen' && (
                                                    <button onClick={() => unfreezeUser(user.id)} className="p-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-500 rounded-lg" title="Unfreeze">
                                                        <Unlock className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            )}

            {/* --- DIVIDENDS TAB (Premium UI) --- */}
            {activeTab === 'dividends' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-1 bg-neutral-900/30 border border-neutral-800 rounded-2xl p-6 backdrop-blur-xs h-fit">
                        <div className="w-12 h-12 bg-emerald-500/10 rounded-2xl flex items-center justify-center text-emerald-500 mb-6 border border-emerald-500/20">
                            <Banknote className="w-6 h-6" />
                        </div>
                        <h2 className="text-xl font-bold mb-2">Yield Distribution</h2>
                        <p className="text-sm text-neutral-400 mb-8 leading-relaxed">
                            Execute automated USDC dividend payouts to all token holders of a specific asset class. Smart contracts handle the snapshot and distribution.
                        </p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs uppercase text-neutral-500 font-bold mb-2">Target Asset Vault</label>
                                <select className="w-full bg-neutral-950 border border-neutral-800 rounded-xl p-3 text-white focus:outline-hidden focus:border-emerald-500 transition-colors">
                                    {assets.filter(a => a.type === 'rwa').map(a => (
                                        <option key={a.id} value={a.id}>{a.name} ({a.symbol})</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs uppercase text-neutral-500 font-bold mb-2">Distribution Amount (USDC)</label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500">$</span>
                                    <input
                                        type="number"
                                        className="w-full bg-neutral-950 border border-neutral-800 rounded-xl p-3 pl-8 text-white focus:outline-hidden focus:border-emerald-500 font-mono transition-colors"
                                        placeholder="50,000.00"
                                    />
                                </div>
                            </div>
                            <button className="w-full py-4 mt-4 bg-emerald-500 text-black font-bold rounded-xl hover:bg-emerald-400 hover:shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all flex items-center justify-center gap-2">
                                <Zap className="w-4 h-4" /> Execute Payout
                            </button>
                        </div>
                    </div>

                    <div className="lg:col-span-2 bg-neutral-900/30 border border-neutral-800 rounded-2xl p-6 backdrop-blur-xs">
                        <h3 className="text-lg font-bold mb-6">Payout History</h3>
                        <div className="rounded-xl overflow-hidden border border-neutral-800">
                            <table className="w-full text-left bg-neutral-950/50">
                                <thead className="bg-neutral-900/50 text-xs uppercase text-neutral-500">
                                    <tr>
                                        <th className="p-4">Event ID</th>
                                        <th className="p-4">Vault</th>
                                        <th className="p-4">Total Payout</th>
                                        <th className="p-4 text-right">Executing Admin</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-neutral-800">
                                    {transactions.filter(t => t.type === 'distribution').map((tx) => (
                                        <tr key={tx.id} className="text-sm">
                                            <td className="p-4 font-mono text-neutral-500">{tx.id.split('-')[1]}</td>
                                            <td className="p-4 font-medium text-white">NYC-RE</td>
                                            <td className="p-4 font-mono text-emerald-500">+${tx.amount.toLocaleString()}</td>
                                            <td className="p-4 text-right flex items-center justify-end gap-2">
                                                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                                System
                                            </td>
                                        </tr>
                                    ))}
                                    {transactions.filter(t => t.type === 'distribution').length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="p-8 text-center text-neutral-500 italic">No distribution events recorded.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* --- FACTORY MODAL (Reused Logic) --- */}
            {isFactoryOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-xs p-4 animate-in fade-in duration-200">
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-neutral-900 border border-neutral-800 p-8 rounded-3xl w-full max-w-lg shadow-2xl shadow-black/50">
                        {/* Wrapper for existing form logic (simplified presentation) */}
                        <div className="flex justify-between items-center mb-8">
                            <h2 className="text-2xl font-bold">Mint New Asset</h2>
                            <button onClick={() => setIsFactoryOpen(false)} className="p-2 hover:bg-neutral-800 rounded-full text-neutral-400 hover:text-white transition-colors"><XCircle className="w-6 h-6" /></button>
                        </div>
                        <form onSubmit={handleIssueAsset} className="space-y-6">
                            {/* ... (Same Form Fields simplified for brevity in this replacement block, but assumes fields exist) ... */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-xs uppercase font-bold text-neutral-500">Name</label>
                                    <input className="w-full bg-neutral-950 border border-neutral-800 p-3 rounded-lg text-white" value={newAsset.name} onChange={e => setNewAsset({ ...newAsset, name: e.target.value })} placeholder="Asset Name" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs uppercase font-bold text-neutral-500">Symbol</label>
                                    <input className="w-full bg-neutral-950 border border-neutral-800 p-3 rounded-lg text-white uppercase font-mono" value={newAsset.symbol} onChange={e => setNewAsset({ ...newAsset, symbol: e.target.value })} placeholder="TICKER" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs uppercase font-bold text-neutral-500">Price ($)</label>
                                <input type="number" className="w-full bg-neutral-950 border border-neutral-800 p-3 rounded-lg text-white font-mono" value={newAsset.price} onChange={e => setNewAsset({ ...newAsset, price: parseFloat(e.target.value) })} placeholder="0.00" />
                            </div>
                            <div className="flex gap-2 pt-4">
                                <button type="button" onClick={() => setNewAsset({ ...newAsset, type: 'rwa' })} className={`flex-1 p-3 rounded-xl border text-sm font-bold ${newAsset.type === 'rwa' ? 'bg-blue-500/20 border-blue-500 text-blue-500' : 'bg-neutral-950 border-neutral-800 text-neutral-500'}`}>RWA</button>
                                <button type="button" onClick={() => setNewAsset({ ...newAsset, type: 'crypto' })} className={`flex-1 p-3 rounded-xl border text-sm font-bold ${newAsset.type === 'crypto' ? 'bg-orange-500/20 border-orange-500 text-orange-500' : 'bg-neutral-950 border-neutral-800 text-neutral-500'}`}>CRYPTO</button>
                            </div>
                            <button type="submit" className="w-full py-4 bg-white text-black font-bold rounded-xl hover:bg-neutral-200 mt-4">Confirm Minting</button>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
}

// --- HELPER COMPONENTS ---
interface StatsCardProps {
    title: string;
    value: string;
    trend: string;
    isAlert?: boolean;
    icon: React.ComponentType<{ className?: string }>;
}

function StatsCard({ title, value, trend, isAlert, icon: Icon }: StatsCardProps) {
    return (
        <div className="bg-neutral-900/40 border border-neutral-800 p-6 rounded-2xl backdrop-blur-xs hover:border-neutral-700 transition-colors group">
            <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-neutral-950 rounded-xl border border-neutral-800 group-hover:border-neutral-700 transition-colors">
                    <Icon className="w-5 h-5 text-neutral-400 group-hover:text-white transition-colors" />
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${isAlert ? 'bg-red-500/20 text-red-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
                    {trend}
                </span>
            </div>
            <div className="text-neutral-500 text-sm font-medium mb-1">{title}</div>
            <div className="text-2xl font-bold tracking-tight font-mono">{value}</div>
        </div>
    );
}


function StatusBadge({ status }: { status: KycStatus }) {
    switch (status) {
        case 'verified':
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-xs font-medium">
                    <CheckCircle className="w-3 h-3" /> Verified
                </span>
            );
        case 'pending':
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20 text-xs font-medium">
                    <AlertTriangle className="w-3 h-3" /> Reviewing
                </span>
            );
        case 'frozen':
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/10 text-blue-500 border border-blue-500/20 text-xs font-medium">
                    <AlertTriangle className="w-3 h-3" /> Frozen
                </span>
            );
        case 'rejected':
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/10 text-red-500 border border-red-500/20 text-xs font-medium">
                    <XCircle className="w-3 h-3" /> Rejected
                </span>
            );
        default:
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-800 text-neutral-400 border border-neutral-700 text-xs font-medium">
                    Unverified
                </span>
            );
    }
}
